import { PageResponse } from '@/models/page-response';
import { Person } from '@/models/person';
import { PersonMinimalDto } from '@/models/person-minimal-dto';
import { parseServerDate } from '@/utils/parse-server-date';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { FilterModel, SortModelItem } from 'ag-grid-community';
import { Observable, OperatorFunction, catchError, map, of, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PersonService {
  readonly personsChanged = signal<Date>(new Date());

  constructor(private httpClient: HttpClient) {}

  getPersonByEmail(email: string): Observable<Person | null> {
    return this.httpClient.get<Person>(`${environment.apiUrl}/api/v1/admin/persons/email/${email}`).pipe(
      mapPerson(),
      catchError(() => of(null)),
    );
  }

  retrieveByToken(token: string): Observable<Person> {
    return this.httpClient.get<Person>(`${environment.apiUrl}/api/v1/persons/invitations/${token}`).pipe(mapPerson());
  }

  getAll(
    page: number,
    itemsPerPage: number,
    sortModel: SortModelItem[],
    filterModel: FilterModel,
  ): Observable<PageResponse<Person>> {
    let params = new HttpParams().set('page', page.toString()).set('itemsPerPage', itemsPerPage.toString());

    if (sortModel.length) {
      params = params.set('sort', JSON.stringify(sortModel));
    }

    if (Object.keys(filterModel).length) {
      params = params.set('filter', JSON.stringify(filterModel));
    }

    return this.httpClient.get<PageResponse<Person>>(`${environment.apiUrl}/api/v1/admin/persons`, {
      params,
    });
  }

  getById(id: string): Observable<Person> {
    return this.httpClient.get<Person>(`${environment.apiUrl}/api/v1/admin/persons/${id}`).pipe(mapPerson());
  }

  create(person: Omit<Person, 'id'>, sendInvitation = false): Observable<Person> {
    const params = new HttpParams().set('sendInvitation', sendInvitation.toString());
    return this.httpClient
      .post<Person>(
        `${environment.apiUrl}/api/v1/admin/persons`,
        {
          ...person,
          id: null,
        },
        { params },
      )
      .pipe(mapPerson(), this.notifyPersonsChanged());
  }

  update(person: Person): Observable<Person> {
    return this.httpClient
      .put<Person>(`${environment.apiUrl}/api/v1/admin/persons/${person.id}`, person)
      .pipe(mapPerson(), this.notifyPersonsChanged());
  }

  delete(id: string): Observable<void> {
    return this.httpClient
      .delete<void>(`${environment.apiUrl}/api/v1/admin/persons/${id}`)
      .pipe(this.notifyPersonsChanged());
  }

  invite(id: string): Observable<void> {
    return this.httpClient
      .post<void>(`${environment.apiUrl}/api/v1/admin/persons/${id}/invite`, {})
      .pipe(this.notifyPersonsChanged());
  }

  getProjectSafetyCoordinators(projectId: string): Observable<PersonMinimalDto[]> {
    return this.httpClient.get<PersonMinimalDto[]>(
      `${environment.apiUrl}/api/v1/Projects/${projectId}/persons/safety-coordinators`,
    );
  }

  private notifyPersonsChanged<T>(): OperatorFunction<T, T> {
    return tap(() => this.personsChanged.set(new Date()));
  }
}

function mapPerson(): (source: Observable<Person>) => Observable<Person> {
  return map((person: Person) => ({
    ...person,
    certificates:
      person.certificates?.map((cert) => ({
        ...cert,
        validUntil: parseServerDate(cert.validUntil),
        issuedOn: parseServerDate(cert.issuedOn),
      })) ?? [],
  }));
}
